import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStoreFeature,
  type,
  withMethods,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, firstValueFrom, of, pipe, switchMap, tap } from 'rxjs';
import { ActiveUserState } from '../user/active-user.state';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthService } from '../../services/account/auth.service';
import { Router } from '@angular/router';
import { HyperionContext } from '../../models/base-entity';
import { DialogService } from 'primeng/dynamicdialog';
import { LoginFormComponent } from '../../components/molecules/login-form/login-form.component';
import { User } from '../../models/user';

/**
 * A (re-)login as a different user than the one whose data is loaded (e.g.
 * via the "Login abgelaufen" dialog) reloads the page, so no stores or
 * permissions of the previous user survive.
 *
 * @return {boolean} true if a reload was triggered
 */
function reloadIfUserChanged(
  previousUser: User | null,
  nextUser: User | null
): boolean {
  if (previousUser && nextUser && previousUser._id !== nextUser._id) {
    window.location.reload();
    return true;
  }
  return false;
}

export function withAuthenticationOperations() {
  return signalStoreFeature(
    {
      state: type<ActiveUserState>(),
    },
    withMethods(
      (
        store,
        authService = inject(AuthService),
        router = inject(Router),
        dialogService = inject(DialogService)
      ) => ({
        // Triggered when login is required
        openLoginDialog(headerText = 'Ihr Login ist abgelaufen') {
          if (!store.dialogRef()) {
            patchState(store, {
              dialogRef: dialogService.open(LoginFormComponent, {
                header: headerText,
                width: '33rem',
                closable: false,
              }),
            });
          }
        },

        // Closes the dialog (e.g., after successful login)
        closeLoginDialog() {
          store.dialogRef()?.close();
          patchState(store, {
            dialogRef: undefined,
          });
        },

        // `targetUrl` should be the URL being navigated TO, e.g. from a
        // router guard's RouterStateSnapshot. Angular resolves canActivate
        // guards before committing the new URL to window.location, so
        // reading window.location.pathname here would still reflect the
        // PREVIOUS route and could skip the auth check entirely when
        // navigating client-side from a public into a protected route.
        checkAuthStatus(
          bypassRedirect = false,
          targetUrl?: string
        ): Promise<boolean> {
          const currentUrl = targetUrl ?? window.location.pathname;

          // Define routes to bypass authentication check
          const BLACKLISTED_ROUTE_PREFIXES: string[] = [
            '/atlas-intern',
            '/data-backend',
            '/dashboard',
          ];

          const requiresAuthCheck = BLACKLISTED_ROUTE_PREFIXES.some(prefix =>
            currentUrl.startsWith(prefix)
          );

          if (!requiresAuthCheck) return Promise.resolve(true);

          // Skip API call if already authenticated and checked within the last 30s
          const AUTH_CACHE_TTL = 30_000;
          const lastChecked = store.lastChecked();
          if (
            store.isLoggedIn() &&
            store.initialized() &&
            lastChecked !== null &&
            Date.now() - lastChecked < AUTH_CACHE_TTL
          ) {
            return Promise.resolve(true);
          }

          patchState(store, { loading: true, error: null });
          return firstValueFrom(
            authService.checkAuthStatus().pipe(
              tap(response => {
                if (reloadIfUserChanged(store.user(), response.user)) {
                  return;
                }
                // Update state if authenticated successfully
                patchState(store, {
                  isLoggedIn: true,
                  user: response.user,
                  loading: false,
                  error: null,
                  initialized: true,
                  impersonatedBy: response.impersonatedBy || null,
                  commune: response.user.commune,
                  lastChecked: Date.now(),
                });

                this.closeLoginDialog(); // Close dialog after success
              }),
              catchError(error => {
                console.error('Authentication check failed:', error);

                // Handle unauthenticated state
                patchState(store, {
                  isLoggedIn: false,
                  loading: false,
                  lastChecked: null,
                  error: {
                    status: error.status,
                    message:
                      'Login abgelaufen. Bitte melden Sie sich erneut an.',
                  },
                });

                // Redirect to login or handle dialog
                //if (!bypassRedirect) router.navigate(['/login']);
                this.openLoginDialog('Login abgelaufen');

                // Rethrow error or return `false` for failed state
                return of(false);
              })
            )
          );
        },
        isLoginActive(): Promise<boolean> {
          return firstValueFrom(
            authService.checkAuthStatus().pipe(
              tap(response => {
                patchState(store, {
                  isLoggedIn: true,
                  user: response.user,
                  loading: false,
                  error: null,
                  impersonatedBy: response.impersonatedBy || null,
                  initialized: true,
                  commune: response.user.commune,
                  lastChecked: Date.now(),
                });
                router.navigate(['/dashboard']);
              }),
              catchError(error => {
                patchState(store, {
                  isLoggedIn: false,
                  loading: false,
                });

                return of(false);
              })
            )
          );
        },

        setContext(context: HyperionContext) {
          patchState(store, { context });
        },
        setPublicCommune(commune: string, slug: string) {
          patchState(store, { commune, slug, context: 'atlasExternal' });
        },
        /**
         * Back in an internal context (internal atlas, data backend) after
         * viewing another commune's public atlas: use the user's own commune
         * again - otherwise X-Commune-ID keeps pointing to the public one.
         */
        restoreOwnCommune() {
          const ownCommune = store.user()?.commune;
          if (ownCommune && store.commune() !== ownCommune) {
            patchState(store, { commune: ownCommune });
          }
        },
        switchUser(userId: string) {
          authService.switchUser(userId).subscribe({
            next: response => {
              window.location.reload();
            },
          });
        },
        switchBack(userId: string) {
          authService.switchBack().subscribe({
            next: response => {
              window.location.reload();
            },
          });
        },
        login: rxMethod<{ username: string; password: string }>(
          pipe(
            switchMap(({ username, password }) => {
              patchState(store, { loading: true, error: null });
              return authService.login({ username, password }).pipe(
                tapResponse({
                  next: response => {
                    if (response.twoFactorRequired) {
                      sessionStorage.setItem(
                        'hyperion_2fa_temp_user_id',
                        response.userId
                      );
                      patchState(store, {
                        tempUserId: response.userId,
                        loading: false,
                        error: null,
                      });
                      router.navigate(['/zwei-faktor-authentifizierung']);
                      return;
                    }

                    store.dialogRef()?.close();

                    if (reloadIfUserChanged(store.user(), response.user)) {
                      return;
                    }
                    patchState(store, {
                      isLoggedIn: true,
                      user: response.user,
                      loading: false,
                      error: null,
                      initialized: true,
                      dialogRef: undefined,
                      commune: response.user.commune,
                      lastChecked: Date.now(),
                    });
                    if (router.url === '/' || router.url === '/login') {
                      router.navigate(['/dashboard']);
                    }
                  },
                  error: (err: HttpErrorResponse) => {
                    const errorMessage =
                      err.status === 404
                        ? 'Benutzer nicht gefunden'
                        : err.message || 'An unknown error occurred';
                    patchState(store, {
                      isLoggedIn: false,
                      user: null,
                      loading: false,
                      error: { status: err.status, message: errorMessage },
                      commune: '',
                      initialized: true,
                    });
                  },
                })
              );
            })
          )
        ),
        verify2FA: rxMethod<{ userId: string; code: string }>(
          pipe(
            switchMap(({ userId, code }) => {
              patchState(store, { loading: true, error: null });
              return authService.verify2FA(userId, code).pipe(
                tapResponse({
                  next: response => {
                    sessionStorage.removeItem('hyperion_2fa_temp_user_id');
                    store.dialogRef()?.close();

                    if (reloadIfUserChanged(store.user(), response.user)) {
                      return;
                    }
                    patchState(store, {
                      isLoggedIn: true,
                      user: response.user,
                      loading: false,
                      error: null,
                      initialized: true,
                      dialogRef: undefined,
                      commune: response.user.commune,
                      twoFactorRequired: false,
                      tempUserId: null,
                      lastChecked: Date.now(),
                    });

                    const currentUrl = router.url;
                    if (
                      currentUrl === '/' ||
                      currentUrl === '/login' ||
                      currentUrl.includes('zwei-faktor-authentifizierung')
                    ) {
                      router.navigate(['/dashboard']);
                    }
                  },
                  error: (err: HttpErrorResponse) => {
                    patchState(store, {
                      loading: false,
                      error: {
                        status: err.status,
                        message:
                          err.error?.message ||
                          'Ungültiger oder abgelaufener Code',
                      },
                    });
                  },
                })
              );
            })
          )
        ),
        logout: rxMethod<{ redirect?: boolean } | void>(
          pipe(
            switchMap((args = { redirect: true }) => {
              const redirect =
                args && 'redirect' in args ? args.redirect : true;

              patchState(store, { loading: true });

              return authService.logout().pipe(
                tapResponse({
                  next: () => {
                    sessionStorage.removeItem('hyperion_2fa_temp_user_id');
                    patchState(store, {
                      isLoggedIn: false,
                      user: null,
                      loading: false,
                      commune: '',
                      initialized: false,
                      twoFactorRequired: false,
                      tempUserId: null,
                      lastChecked: null,
                    });

                    // Full page load instead of client-side navigation: it
                    // drops every entity store, the permission map and the
                    // handlers' `initialized` flags, so the next login (maybe
                    // by someone else) never sees the previous user's data.
                    if (redirect) {
                      window.location.assign('/login');
                    } else {
                      window.location.reload();
                    }
                  },
                  error: (err: HttpErrorResponse) => {
                    const errorMessage =
                      err.message || 'An unknown error occurred';
                    patchState(store, {
                      isLoggedIn: false,
                      user: null,
                      loading: false,
                      error: { status: err.status, message: errorMessage },
                      commune: '',
                      initialized: false,
                    });
                  },
                })
              );
            })
          )
        ),
        loginRedirect(): void {
          router.navigate(['/login']);
        },
      })
    )
  );
}
